import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable, DataTableError, DataTablePaginator } from '@utmost.co/utmost-design-system';
import { useTypedQuery } from 'src/utils/hooks/useTypedQuery';
import { filterNotEmpty } from 'src/utils/data/listHelpers';
import { useDataTableFiltersEvent } from 'src/integrations/mixpanel/hooks/useDataTableFiltersEvent';
import { useDataTableColumnConfigurations } from 'src/utils/hooks/useDataTableColumnConfigurations/useDataTableColumnConfigurations';
import { StatusModel } from 'src/graphql/types';
import { GetCustomFieldsDefinitionsDocument } from './utils/__generated__/GetCustomFieldDefinitions.graphql';
import { CustomFieldDefinition, CustomFieldDefinitionQuery } from './utils/CustomFields.types';
import { useCustomFieldDefinitionsColumns } from './Columns/useCustomFieldDefinitionsColumns';
import { CustomFieldDefinitionsToolbar } from './CustomFieldDefinitionsToolbar';
import { CustomFieldDefinitionsFilters } from './CustomFieldDefinitionsFilters';
import { SaveCustomFieldDefinitionDialog } from './SaveCustomField/SaveCustomFieldDialog';
import { DeleteCustomFieldDialog } from './Columns/DeleteCustomFieldDialog';
import { useHistory, useLocation } from 'react-router';

export const CustomFieldDefinitions: FC<React.PropsWithChildren<unknown>> = () => {
  const { get: getCustomFields, data, loading, error, refetch } = useTypedQuery(GetCustomFieldsDefinitionsDocument);
  const [variables, setVariables] = useState<CustomFieldDefinitionQuery>();
  const [saveCustomFieldDefinitionModal, setSaveCustomFieldDefinitionModal] = useState<boolean>(false);
  const [deleteCustomFieldDefinitionModal, setDeleteCustomFieldDefinitionModal] = useState<boolean>(false);
  const [customFieldToEdit, setCustomFieldToEdit] = useState<CustomFieldDefinition>();
  const { columnConfiguration, patchColumnConfiguration } = useDataTableColumnConfigurations('SETTINGS_CUSTOM_FIELDS');
  useDataTableFiltersEvent<DeepPartial<CustomFieldDefinitionQuery>>(variables, 'SETTINGS_CUSTOM_FIELDS');

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    if (variables?.page?.limit) {
      getCustomFields({
        variables: {
          search: variables?.query?.search,
          statuses: [StatusModel.Active, StatusModel.Inactive],
          limit: variables?.page?.limit,
          offset: variables?.page?.offset,
          parentEntityType: variables?.query?.parentEntityType
        }
      });
    }
    // exclude getCustomFields to avoid unnecessary re-renders
  }, [variables]);

  const customFields = useMemo(
    () => filterNotEmpty(data?.customFieldDefinitions?.customFieldDefinitions) || [],
    [data?.customFieldDefinitions?.customFieldDefinitions]
  );
  const totalCount = data?.customFieldDefinitions?.totalCount ?? 0;
  const pageDataCount = customFields?.length ?? 0;

  const onEditHandler = useCallback((customDefinition: CustomFieldDefinition) => {
    setCustomFieldToEdit(customDefinition);
    setSaveCustomFieldDefinitionModal(true);
  }, []);

  const onCloseHandler = useCallback(() => {
    setCustomFieldToEdit(undefined);
    setSaveCustomFieldDefinitionModal(false);
    setDeleteCustomFieldDefinitionModal(false);
  }, []);

  const onDeleteHandler = useCallback((customDefinition: CustomFieldDefinition) => {
    setCustomFieldToEdit(customDefinition);
    setDeleteCustomFieldDefinitionModal(true);
  }, []);

  const columns = useCustomFieldDefinitionsColumns({
    onEdit: onEditHandler,
    onSubmitDone: refetch,
    onDelete: onDeleteHandler
  });
  return (
    <>
      <DataTable
        isFetching={loading}
        onVariablesChanged={setVariables}
        onColumnConfigurationChanged={patchColumnConfiguration}
        initialState={columnConfiguration}
        totalCount={totalCount}
        columns={columns}
        data={customFields}
        querySettings={{ manualSortBy: true, useParams: true, previousURLKey: 'settings.customFieldDefinitions.v1' }}
      >
        <DataTableError
          error={!customFields?.length && error?.message}
          useHistory={useHistory}
          useLocation={useLocation}
        />
        <CustomFieldDefinitionsFilters query={variables?.query} />
        <CustomFieldDefinitionsToolbar
          variables={variables}
          totalCount={totalCount}
          pageDataCount={pageDataCount}
          onCustomFieldAdd={() => setSaveCustomFieldDefinitionModal(true)}
        />

        <DataTablePaginator />
      </DataTable>
      <DeleteCustomFieldDialog
        refetch={refetch}
        customDefinition={customFieldToEdit}
        open={deleteCustomFieldDefinitionModal}
        onClose={onCloseHandler}
      />
      {saveCustomFieldDefinitionModal && (
        <SaveCustomFieldDefinitionDialog
          customField={customFieldToEdit}
          mode={customFieldToEdit ? 'edit' : 'add'}
          refetch={refetch}
          onClose={onCloseHandler}
        />
      )}
    </>
  );
};
